import React from 'react';
import { Box, Grid, Card, CardHeader, CardContent, CardActions, Button, Chip, Stack, Typography, Divider } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ReviewsIcon from '@mui/icons-material/Reviews';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Box>
);

const StepList = ({ steps }) => (
  <ol style={{ margin: 0, paddingInlineStart: 18 }}>
    {steps.map((s, i) => (
      <li key={i} style={{ marginBottom: 8 }}>
        <Typography variant="body2">{s}</Typography>
      </li>
    ))}
  </ol>
);

export default function ExtensionsPage() {
  const backendUrl = 'https://autolistify-new.onrender.com';
  const productImportPath = 'extension/';
  const reviewsPath = 'backend/taoextractorextension/';

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>Extensions</Typography>
      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Install our Chrome extensions to boost your workflow. Follow the steps below to load them in Developer Mode.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardHeader
              avatar={<CloudUploadIcon color="primary" />}
              title={<Typography variant="h6" sx={{ fontWeight: 700 }}>Taobao to Shopify Importer</Typography>}
              subheader="One-click import of Taobao/Tmall/1688 products into Shopify"
            />
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label="Chrome" color="primary" size="small" />
                <Chip label="Manifest v3" variant="outlined" size="small" />
              </Stack>

              <Section title="Install (Developer Mode)">
                <StepList
                  steps={[
                    'Download or clone this repository to your computer.',
                    'Open Chrome and visit chrome://extensions',
                    'Enable "Developer mode" (top-right).',
                    `Click "Load unpacked" and select the folder: ${productImportPath}`,
                    'Pin the extension for quick access.',
                  ]}
                />
              </Section>

              <Section title="After Install">
                <StepList
                  steps={[
                    'Click the extension icon > it will default to your store domain (configurable).',
                    `Update API endpoint to production if needed. Current background uses localhost; set to: ${backendUrl}`,
                    'On a Taobao/Tmall/1688 product page, click the extension to import the product into your connected Shopify store.',
                  ]}
                />
              </Section>

              <Section title="Production API URL">
                <Box sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 13, overflowX: 'auto' }}>
                  {backendUrl}
                </Box>
              </Section>
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2 }}>
              <Button variant="contained" color="primary" href="#" onClick={(e) => { e.preventDefault(); window.open('chrome://extensions', '_blank'); }}>Open Extensions</Button>
              <Button variant="outlined" href={`https://github.com/AminB101/autolistify-1/tree/main/${productImportPath}`} target="_blank" rel="noreferrer">View Source</Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardHeader
              avatar={<ReviewsIcon color="primary" />}
              title={<Typography variant="h6" sx={{ fontWeight: 700 }}>Taobao Product Extractor (Reviews)</Typography>}
              subheader="Extract detailed product info and reviews from Taobao/Tmall/1688"
            />
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label="Chrome" color="primary" size="small" />
                <Chip label="Manifest v3" variant="outlined" size="small" />
              </Stack>

              <Section title="Install (Developer Mode)">
                <StepList
                  steps={[
                    'Download or clone this repository to your computer.',
                    'Open Chrome and visit chrome://extensions',
                    'Enable "Developer mode" (top-right).',
                    `Click "Load unpacked" and select the folder: ${reviewsPath}`,
                    'Pin the extension for quick access.',
                  ]}
                />
              </Section>

              <Section title="Send Reviews to Backend">
                <StepList
                  steps={[
                    `Ensure the backend URL is set to production: ${backendUrl}`,
                    'From a product page, use the extension to extract and send reviews to your connected store (Judge.me integration).',
                  ]}
                />
              </Section>

              <Section title="Paths">
                <Typography variant="body2" sx={{ mb: 1 }}>Unpacked folder paths in this repo:</Typography>
                <Box sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 13, overflowX: 'auto' }}>
                  {reviewsPath}
                </Box>
              </Section>
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2 }}>
              <Button variant="contained" color="primary" href="#" onClick={(e) => { e.preventDefault(); window.open('chrome://extensions', '_blank'); }}>Open Extensions</Button>
              <Button variant="outlined" href={`https://github.com/AminB101/autolistify-1/tree/main/${reviewsPath}`} target="_blank" rel="noreferrer">View Source</Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Tip: If Chrome blocks opening chrome://extensions from a site, copy the URL and paste it manually in the address bar.
        </Typography>
      </Box>
    </Box>
  );
}
